import { Cancel, CheckCircle } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';

import { breedLib } from 'entities/breed';

import { Breed, ESpecie } from 'shared/api';
import { useCheckCodeUnique } from 'shared/lib';

const validationSchema = yup.object({
  _id: yup.string(),
  specie: yup.mixed<ESpecie>().oneOf(Object.values(ESpecie)).required('Обязательное поле'),
  code: yup.string().required('Обязательное поле'),
  name: yup.string().required('Обязательное поле'),
  name_ru: yup.string().required('Обязательное поле'),
  descriptions: yup.string(),
  descriptions_ru: yup.string(),
  verified: yup.boolean(),
});

type Props = {
  title: string;
  isOpen: boolean;
  breed?: Breed;
  onClose: () => void;
  onSubmit: (data: Partial<Breed>) => void;
  isLoading: boolean;
};

export const BreedDialog = ({ title, isOpen, breed, onClose, onSubmit, isLoading }: Props) => {
  const formik = useFormik({
    initialValues: {
      _id: breed?._id ?? '',
      specie: breed?.specie ?? ESpecie.Dog,
      code: breed?.code ?? '',
      name: breed?.name ?? '',
      name_ru: breed?.name_ru ?? '',
      descriptions: breed?.descriptions ?? '',
      descriptions_ru: breed?.descriptions_ru ?? '',
      verified: breed?.verified ?? false,
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const codeCheck = useCheckCodeUnique(
    'breed',
    formik.values.code,
    breed?._id,
    isOpen && formik.values.code.trim().length > 0,
  );

  useEffect(() => {
    if (isOpen) {
      codeCheck.reset();
      if (breed) {
        void formik.setValues({
          _id: breed._id,
          specie: breed.specie,
          code: breed.code,
          name: breed.name,
          name_ru: breed.name_ru,
          descriptions: breed.descriptions ?? '',
          descriptions_ru: breed.descriptions_ru ?? '',
          verified: breed.verified,
        });
      } else {
        formik.resetForm();
      }
    }
    // eslint-disable-next-line
  }, [breed, isOpen]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Specie */}
            <FormControl fullWidth error={formik.touched.specie && Boolean(formik.errors.specie)}>
              <InputLabel>Specie *</InputLabel>
              <Select
                name="specie"
                label="Specie *"
                value={formik.values.specie}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value={ESpecie.Dog}>{breedLib.getSpecieText(ESpecie.Dog)}</MenuItem>
                <MenuItem value={ESpecie.Cat}>{breedLib.getSpecieText(ESpecie.Cat)}</MenuItem>
              </Select>
            </FormControl>

            {/* Code */}
            <TextField
              fullWidth
              name="code"
              label="Code *"
              value={formik.values.code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={codeCheck.isDuplicate ? true : formik.touched.code && Boolean(formik.errors.code)}
              helperText={
                codeCheck.isDuplicate ? 'This code is already in use' : formik.touched.code && formik.errors.code
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {codeCheck.isChecking && <CircularProgress size={20} />}
                    {codeCheck.isUnique && <CheckCircle color="success" />}
                    {codeCheck.isDuplicate && <Cancel color="error" />}
                  </InputAdornment>
                ),
              }}
            />

            {/* Name EN */}
            <TextField
              fullWidth
              name="name"
              label="Name (EN) *"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            {/* Name RU */}
            <TextField
              fullWidth
              name="name_ru"
              label="Name (RU) *"
              value={formik.values.name_ru}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name_ru && Boolean(formik.errors.name_ru)}
              helperText={formik.touched.name_ru && formik.errors.name_ru}
            />

            {/* Description EN */}
            <TextField
              fullWidth
              name="descriptions"
              label="Description (EN)"
              multiline
              rows={3}
              value={formik.values.descriptions}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            {/* Description RU */}
            <TextField
              fullWidth
              name="descriptions_ru"
              label="Description (RU)"
              multiline
              rows={3}
              value={formik.values.descriptions_ru}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            {/* Verified */}
            <FormControlLabel
              control={<Checkbox name="verified" checked={formik.values.verified} onChange={formik.handleChange} />}
              label="Verified"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isLoading} disabled={codeCheck.isDuplicate}>
            {breed ? 'Update' : 'Create'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
